import React, { useEffect, useState } from 'react'
import { MeetingProvider } from '../utils/meetingProvider'
import { getMeetingProvider } from '../../utils'
import { useCustomFetch } from '../../hooks/useCustomFetch'
import cnt from '../../constants'
import { formatDate } from '../../utils/formatDate'
import toast from 'react-hot-toast'
import { CloseIcon } from '../icons/Close'
import { StarIcon } from '../icons/Star'
import { TrashIcon } from '../icons/Trash'
import { ProjectIcon } from '../icons/Project'
import { LinkIcon } from '../icons/Link'
import MultiTag from '../tags/MultiTag'
import { OriginalTranscriptCard } from '../project/OriginalTranscriptCard'
import RayIcon from '../icons/Ray'

// Import ShadCN UI components
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

function getColor(entity) {
  return (
    entity.color ||
    `${entity.sentiment_analysis === 'positive' ? '#BBE6B7' : entity.sentiment_analysis === 'negative' ? '#E5A8AC' : '#FFF98E'}`
  )
}

const Titles = {
  note: 'Need',
  feedback: 'Feedback',
  quote: 'Quote',
  problem: 'Pain-Point',
}

// Section component using ShadCN components
function Section({ title, children }) {
  return (
    <div className="py-4">
      {!!title && (
        <h4 className="mb-2 text-muted-foreground text-xs font-extrabold uppercase tracking-wide">{title}</h4>
      )}
      {children}
      <Separator className="mt-4" />
    </div>
  )
}

// Icon Button component using ShadCN
function IconButton({ onClick, icon: Icon, checked }) {
  return (
    <Button variant="outline" size="icon" className="w-16 h-10 border border-slate-300 shadow" onClick={onClick}>
      <Icon checked={checked} />
    </Button>
  )
}

export function EditDialog({
  entity,
  entityName,
  participants,
  setEntities,
  meeting,
  project,
  categories,
  setProject,
  close,
  readOnly = false,
  rating = true,
  tagging = true,
  changeContent,
  changeCategory,
  changeColor,
  deleteEntity,
}) {
  const [content, setContent] = useState('')
  const [open, setOpen] = useState(true)
  const customFetch = useCustomFetch()
  // Close the dialog
  const handleClose = () => {
    setOpen(false)
    setTimeout(close, 200) // Delay to allow animation
  }

  async function handleToggleStar() {
    try {
      await customFetch(`/update${capitalizeFirst(entityName)}Star`, 'PUT', {
        [`${entityName}Id`]: entity.id,
        star: !entity.star,
      })
      setEntities((prevNotes) => prevNotes.map((n) => (n.id === entity.id ? { ...n, star: !n.star } : n)))
    } catch (error) {
      console.log(error)
      toast.error('Whoops! Something went wrong. Please try again.')
    }
  }

  function handleCategoryChange(value) {
    changeCategory(value)
  }

  function handleColorChange(value) {
    changeColor(value)
  }

  function handleContentChange(event) {
    const newContent = event.target.value
    setContent(newContent)
    changeContent(newContent)
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter') handleClose()
  }

  function handleCopyLink() {
    // Create a link for the current entity
    const link = `${window.location.origin}/entity/${entityName}/${entity.id}`
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success('Link copied to clipboard!')
      })
      .catch(() => {
        toast.error('Failed to copy link')
      })
  }

  useEffect(() => {
    if (content !== entity.content) setContent(entity.content)
  }, [entity.content])

  // Function to render the content based on entity type
  const renderEntityContent = () => {
    if (entityName === 'problem') {
      return (
        <div className="flex flex-col items-center justify-center mb-12 pt-6">
          <div
            className="w-[500px] py-10 px-8 bg-white rounded-lg shadow"
            style={{
              height: 'auto',
              minHeight: '300px',
            }}
          >
            <div className="flex justify-center mb-4">
              <RayIcon />
            </div>
            <h3 className="font-semibold text-lg mb-4 text-[#FF4A8E] text-center mt-2 capitalize">
              {entity.title || entity.referent || ''}
            </h3>
            <p className="text-center text-black text-base font-semibold leading-relaxed">{content}</p>
          </div>
        </div>
      )
    }

    // Default rendering for other entity types (note, feedback, quote)
    return (
      <div className="flex flex-col items-center justify-center mb-12 pt-6">
        <Textarea
          value={content}
          readOnly={readOnly}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          style={{
            resize: 'none',
            backgroundColor: getColor(entity),
            height: '300px',
            width: '500px',
          }}
          className={cn(
            'py-10 px-8 shadow rounded-lg hideScrollbar',
            'text-slate-800 font-semibold',
            content.length < 120 ? 'text-3xl leading-relaxed tracking-wider' : 'text-2xl leading-relaxed tracking-wide'
          )}
        />
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-[90%] w-[90%] h-[86%] p-0 rounded-2xl"
        onInteractOutside={(e) => {
          e.preventDefault()
          handleClose()
        }}
        hideCloseButton={true}
      >
        <div className="h-full flex overflow-hidden">
          {/* Main content area - single scroll area for the entire left side */}
          <div
            className="overflow-y-auto h-full p-8 pb-16 bg-gray-50 rounded-l-2xl hideScrollbar"
            style={{ width: 'calc(100% - 352px)' }}
          >
            <div className="flex flex-col h-full">
              <DialogHeader className="space-y-0 text-left mb-10">
                <DialogTitle className="text-[#3f424a] text-4xl font-bold font-['DM_Sans'] leading-[41.40px]">
                  {Titles[entityName]} Details
                </DialogTitle>
              </DialogHeader>

              <div
                className={`flex-1 ${!entity.original_transcript_segment ? 'flex items-center justify-center' : ''}`}
              >
                <div className={!entity.original_transcript_segment ? '-mt-[150px]' : ''}>{renderEntityContent()}</div>
              </div>

              {/* Citations area */}
              {entity.original_transcript_segment && (
                <div className="mb-8">
                  <div className="mb-6 opacity-30">
                    <Separator />
                  </div>
                  <OriginalTranscriptCard note={entity} meeting={meeting} participants={participants} />
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-[352px] border-l border-gray-200 flex flex-col h-full overflow-hidden">
            <ScrollArea className="h-full w-full">
              <div className="px-6 py-5 pr-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-slate-600 text-sm font-semibold uppercase">{Titles[entityName]} Options</h3>
                  <Button variant="ghost" size="icon" onClick={handleClose} className="h-6 w-6">
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <Separator className="mb-4" />

                {(rating || deleteEntity) && (
                  <Section title="">
                    <div className="flex gap-3">
                      {rating && <IconButton onClick={handleToggleStar} icon={StarIcon} checked={entity.star} />}
                      {/* TODO: Copy Link button temporarily disabled - will be re-enabled when sharing functionality is updated */}
                      {/* <IconButton onClick={handleCopyLink} icon={LinkIcon} /> */}
                      {deleteEntity && <IconButton onClick={deleteEntity} icon={TrashIcon} />}
                    </div>
                  </Section>
                )}

                {changeColor && entityName !== 'problem' && (
                  <Section title="Note Color">
                    <Select value={entity.color} onValueChange={handleColorChange}>
                      <SelectTrigger className="w-full">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded-full border border-slate-600"
                            style={{ backgroundColor: entity.color }}
                          ></div>
                          <span className="text-slate-800">
                            {Object.keys(cnt.colors).find((colorName) => cnt.colors[colorName] === entity.color) ||
                              'Custom'}
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(cnt.colors).map((color) => (
                          <SelectItem key={color} value={cnt.colors[color]}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full border border-slate-600"
                                style={{ backgroundColor: cnt.colors[color] }}
                              ></div>
                              <span>{color}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Section>
                )}

                {!!changeCategory && (
                  <Section title="Category">
                    <Select value={entity.category_id} onValueChange={handleCategoryChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.content}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Section>
                )}

                {tagging && (
                  <Section title="Tags">
                    <MultiTag
                      entity={entity}
                      setEntities={setEntities}
                      project={project}
                      setProject={setProject}
                      entityName={entityName}
                    />
                  </Section>
                )}

                {entity.created_by && (
                  <Section title="Created By">
                    <p className="text-slate-800 text-lg font-bold leading-tight tracking-wide">{entity.created_by}</p>
                  </Section>
                )}

                <Section title="Created">
                  <p className="text-slate-800 text-lg font-bold leading-tight tracking-wide">
                    {formatDate(entity.created_at)}
                  </p>
                </Section>

                {project && (
                  <Section title="Project">
                    <div className="flex items-center gap-2">
                      <ProjectIcon />
                      <p className="text-slate-800 text-lg font-bold leading-tight tracking-wide">{project.name}</p>
                    </div>
                  </Section>
                )}

                {!!Object.keys(meeting).length && (
                  <Section title="Session">
                    <div className="flex items-center gap-2">
                      <MeetingProvider
                        provider={!meeting.thread_id ? 'custom' : getMeetingProvider(meeting.meeting_url)}
                      />
                      <p className="text-slate-800 text-lg font-bold leading-tight tracking-wide">{meeting.name}</p>
                    </div>
                  </Section>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function capitalizeFirst(str) {
  return str[0].toUpperCase() + str.substring(1).toLowerCase()
}
